import {
  INJUNCTION_CATEGORY_CONFIG,
  type InjunctionCategorySummary,
} from "./calculateInjunctionCategorySummaries";
import { formatLessons } from "./lessonLimits";

/**
 * To forskellige slags overskud, som ikke må blandes sammen i teksterne:
 *
 * A. Over kategoriloftet. Kan kategorien overføres, havner overskuddet i
 *    `transferredToNext` (intet går tabt) — ellers i `droppedLessons` (bortfalder).
 * B. Over lektionskravet. Kan kategorien ikke overføres, bliver overskuddet
 *    indberettet, fordi dokumentationen ikke kan deles op. Der beskæres altså IKKE
 *    ned til kravet — samme regel som den gamle formular på main.
 */

/** "a", "a og b", "a, b og c" — så listen kan læses højt uden at hakke. */
export const formatLabelList = (labels: string[]): string => {
  const lowered = labels.map((label) => label.toLowerCase());
  if (lowered.length < 2) {
    return lowered.join("");
  }

  return `${lowered.slice(0, -1).join(", ")} og ${lowered[lowered.length - 1]}`;
};

/** Kategorier, hvis overskud aldrig kan overføres — kilden til overskud B. */
const getNonTransferableLabels = (): string =>
  formatLabelList(
    INJUNCTION_CATEGORY_CONFIG.filter(
      (category) => !category.canTransferLessons,
    ).map((category) => category.label),
  );

export const getSurplusLessons = (
  countedLessons: number,
  requiredLessons: number,
): number => Math.max(countedLessons - requiredLessons, 0);

/**
 * Overskud B — forklarer, hvorfor der indberettes mere end lektionskravet.
 * Efteruddannelsesperioden har sine egne kategorier (dispensation kan heller ikke
 * overføres der), så den sender sine egne etiketter ind.
 */
export const getSurplusNotice = (
  surplusLessons: number,
  requiredLessons: number,
  nonTransferableLabels: string = getNonTransferableLabels(),
): string | null => {
  if (surplusLessons <= 0) {
    return null;
  }

  return `Du indberetter ${formatLessons(surplusLessons)} lektioner mere end lektionskravet på ${formatLessons(requiredLessons)}. Lektioner fra ${nonTransferableLabels} kan ikke overføres til næste periode, og dokumentationen kan ikke deles op.`;
};

/**
 * Kun de felter, teksterne bruger. Efteruddannelsesperioden har sin egen
 * summary-type, og begge flows skal kunne bruge samme formuleringer.
 */
export type CategoryLimitSummary = Pick<
  InjunctionCategorySummary,
  | "totalLessons"
  | "maxReported"
  | "submittedLessons"
  | "transferredToNext"
  | "droppedLessons"
>;

/**
 * Fast etiket på kategorier, der ikke kan overføres til næste periode. Vises altid,
 * ikke kun når noget bortfalder: brugeren ser kursus og undervisning blive overført
 * lige ved siden af, og skal kunne se forskellen uden først at ramme loftet.
 * Samme formulering som den gamle formular på main (EfterUddFormCol).
 */
export const NON_TRANSFERABLE_LABEL = "Kan ikke overføres";

/** Overskud A, hvor lektionerne går tabt. Vises som advarsel. */
export const getCategoryLimitWarning = (
  summary: CategoryLimitSummary | null | undefined,
): string | null => {
  // droppedLessons opstår ved at overskride maxReported — så det er DET loft, der
  // skal nævnes. maxTransfer er et andet tal og ville forvirre mere end det hjalp.
  if (
    !summary ||
    summary.droppedLessons <= 0 ||
    summary.maxReported === undefined
  ) {
    return null;
  }

  return `Du har valgt ${formatLessons(summary.totalLessons)} lektioner. Kategoriens maksimum er ${formatLessons(summary.maxReported)}, så ${formatLessons(summary.droppedLessons)} lektioner bortfalder.`;
};

/**
 * Overskud A, hvor lektionerne overføres. Bevidst ikke en advarsel: intet går
 * tabt, og gul på "48 lektioner er i behold" lærer brugeren at overse den gule
 * boks, der betyder, at lektioner bortfalder.
 */
export const getCategoryLimitNotice = (
  summary: CategoryLimitSummary | null | undefined,
): string | null => {
  if (
    !summary ||
    summary.droppedLessons > 0 ||
    summary.transferredToNext <= 0
  ) {
    return null;
  }

  return `Du har valgt ${summary.totalLessons} lektioner. ${summary.submittedLessons} indberettes på afgørelsen, og ${summary.transferredToNext} overføres til næste periode.`;
};
